// Sign-in step 1: work email + password. People are added by an admin (Users); a forgotten password is reset by
// an admin too. The answer says what comes next (the code, a new password, the 2-step set-up, or in).
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { Field } from '@the-kit/ui'
import { SignInInput } from '@/api/schemas'
import { ErrorText } from '@/components/ErrorText'
import { useSignIn } from '../useSession'
import { AuthLayout } from './AuthLayout'

export function PasswordStep() {
  const signIn = useSignIn()
  const [showPassword, setShowPassword] = useState(false)
  const form = useForm<SignInInput>({
    resolver: zodResolver(SignInInput),
    defaultValues: { email: '', password: '' },
  })
  const { errors } = form.formState
  const submit = form.handleSubmit((input) => signIn.mutate(input))
  return (
    <AuthLayout title="Sign in" subtitle="The campaign portal, for the Firstfund team">
      <form onSubmit={submit} noValidate data-testid="sign-in-form">
        <Field label="Work email" error={errors.email?.message}>
          {(id) => (
            <div className="relative">
              <Mail size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                id={id}
                className="input pl-8"
                type="email"
                placeholder="you@firstfundfincorp.com"
                autoComplete="username"
                autoFocus
                {...form.register('email')}
                data-testid="sign-in-email"
              />
            </div>
          )}
        </Field>
        <Field label="Password" error={errors.password?.message}>
          {(id) => (
            <div className="relative">
              <Lock size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                id={id}
                className="input pl-8 pr-9"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                {...form.register('password')}
                data-testid="sign-in-password"
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPassword((shown) => !shown)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          )}
        </Field>
        <ErrorText error={signIn.error} />
        <button
          type="submit"
          className="btn btn-primary mt-2 w-full"
          disabled={signIn.isPending}
          data-testid="sign-in-submit"
        >
          {signIn.isPending ? 'Signing in…' : 'Continue'}
        </button>
        <p className="pt-3 text-center text-2xs text-muted">
          Forgot your password? Ask an admin to reset it.
        </p>
      </form>
    </AuthLayout>
  )
}
